import React from 'react';
import './SalonProfileSkeleton.css';

/**
 * Skeleton component for the Salon Profile Page (/salon/{id})
 * Mirrors the real salon layout on mobile: hero carousel, name + rating + Book button,
 * optional bio, Services (tabs + cards), Team (vertical list of avatar + info rows),
 * and Info (hours + map). See SalonProfilePage.js:2071-2411 for the real structure.
 */
export const SalonProfileSkeleton = () => (
  <div className="salon-profile-skeleton">
    {/* Hero — full-width carousel, back button top-left, share/heart top-right, dots */}
    <div className="skeleton-hero-section">
      <div className="skeleton-hero-image"></div>
      <div className="skeleton-hero-nav">
        <div className="skeleton-back-button"></div>
        <div className="skeleton-hero-actions">
          <div className="skeleton-action-button"></div>
          <div className="skeleton-action-button"></div>
        </div>
      </div>
      <div className="skeleton-carousel-dots">
        <div className="skeleton-dot"></div>
        <div className="skeleton-dot"></div>
        <div className="skeleton-dot"></div>
      </div>
    </div>

    <div className="skeleton-profile-content">
      {/* Profile header: name, rating row, full-width Book button */}
      <div className="skeleton-salon-info">
        <div className="skeleton-salon-name"></div>
        <div className="skeleton-salon-rating">
          <div className="skeleton-pro-rating-score"></div>
          <div className="skeleton-pro-rating-stars"></div>
          <div className="skeleton-pro-rating-count"></div>
        </div>
      </div>
      <div className="skeleton-book-button"></div>

      {/* Bio paragraph */}
      <div className="skeleton-bio">
        <div className="skeleton-text-line"></div>
        <div className="skeleton-text-line"></div>
        <div className="skeleton-text-line short"></div>
      </div>

      {/* Services — title, category tabs, bordered service cards */}
      <div className="skeleton-section">
        <div className="skeleton-section-title"></div>
        <div className="skeleton-category-tabs">
          <div className="skeleton-category-tab active"></div>
          <div className="skeleton-category-tab"></div>
          <div className="skeleton-category-tab"></div>
          <div className="skeleton-category-tab"></div>
        </div>
        <div className="skeleton-services-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton-service-card">
              <div className="skeleton-service-info">
                <div className="skeleton-service-name"></div>
                <div className="skeleton-service-duration"></div>
              </div>
              <div className="skeleton-service-right">
                <div className="skeleton-service-price"></div>
                <div className="skeleton-service-plus"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team — vertical list of team cards (60px avatar + info column) */}
      <div className="skeleton-section">
        <div className="skeleton-section-title"></div>
        <div className="skeleton-team-cards-list">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton-team-card-row">
              <div className="skeleton-team-avatar-round"></div>
              <div className="skeleton-team-info">
                <div className="skeleton-team-header-row">
                  <div className="skeleton-team-name-line"></div>
                  <div className="skeleton-team-rating-line"></div>
                </div>
                <div className="skeleton-team-role-line"></div>
                <div className="skeleton-team-desc-line"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info — hours, address, map, directions */}
      <div className="skeleton-section">
        <div className="skeleton-section-title"></div>
        <div className="skeleton-hours-list">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton-hours-row">
              <div className="skeleton-day"></div>
              <div className="skeleton-time"></div>
            </div>
          ))}
        </div>
        <div className="skeleton-address"></div>
        <div className="skeleton-map"></div>
        <div className="skeleton-directions"></div>
      </div>
    </div>

    {/* Bottom CTA */}
    <div className="skeleton-bottom-cta">
      <div className="skeleton-cta-button"></div>
    </div>
  </div>
);

/**
 * Skeleton component for the Professional Profile Page (/salon/{id}/professional/{id})
 * Mirrors the real stylist layout on mobile: hero image, profile row (avatar + name/title/rating),
 * full-width Book button, optional bio, horizontal portfolio strip, services (tabs + cards),
 * and info (hours + map). See SalonProfilePage.js:2071-2411 for the real structure.
 */
export const ProfessionalProfileSkeleton = () => (
  <div className="salon-profile-skeleton professional-skeleton">
    {/* Hero — full-width image, back button top-left, share/heart top-right */}
    <div className="skeleton-hero-section professional-hero">
      <div className="skeleton-hero-image"></div>
      <div className="skeleton-hero-nav">
        <div className="skeleton-back-button"></div>
        <div className="skeleton-hero-actions">
          <div className="skeleton-action-button"></div>
          <div className="skeleton-action-button"></div>
        </div>
      </div>
    </div>

    <div className="skeleton-profile-content professional-content">
      {/* Profile header — avatar on left, job title + name + rating stacked on right */}
      <div className="skeleton-professional-header">
        <div className="skeleton-pro-avatar"></div>
        <div className="skeleton-pro-info">
          <div className="skeleton-pro-badge"></div>
          <div className="skeleton-pro-name"></div>
          <div className="skeleton-pro-rating">
            <div className="skeleton-pro-rating-score"></div>
            <div className="skeleton-pro-rating-stars"></div>
            <div className="skeleton-pro-rating-count"></div>
          </div>
        </div>
      </div>

      {/* Full-width Book button */}
      <div className="skeleton-book-button"></div>

      {/* Bio paragraph */}
      <div className="skeleton-bio">
        <div className="skeleton-text-line"></div>
        <div className="skeleton-text-line"></div>
        <div className="skeleton-text-line short"></div>
      </div>

      {/* Portfolio — horizontal scroll of portrait cards */}
      <div className="skeleton-section">
        <div className="skeleton-portfolio-scroll">
          <div className="skeleton-portfolio-card"></div>
          <div className="skeleton-portfolio-card"></div>
          <div className="skeleton-portfolio-card"></div>
          <div className="skeleton-portfolio-card"></div>
          <div className="skeleton-portfolio-card"></div>
        </div>
      </div>

      {/* Services — title, category tabs, service cards */}
      <div className="skeleton-section">
        <div className="skeleton-section-title"></div>
        <div className="skeleton-category-tabs">
          <div className="skeleton-category-tab active"></div>
          <div className="skeleton-category-tab"></div>
          <div className="skeleton-category-tab"></div>
          <div className="skeleton-category-tab"></div>
        </div>
        <div className="skeleton-services-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton-service-card">
              <div className="skeleton-service-info">
                <div className="skeleton-service-name"></div>
                <div className="skeleton-service-duration"></div>
              </div>
              <div className="skeleton-service-right">
                <div className="skeleton-service-price"></div>
                <div className="skeleton-service-plus"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info — hours, address, map, directions */}
      <div className="skeleton-section">
        <div className="skeleton-section-title"></div>
        <div className="skeleton-hours-list">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton-hours-row">
              <div className="skeleton-day"></div>
              <div className="skeleton-time"></div>
            </div>
          ))}
        </div>
        <div className="skeleton-address"></div>
        <div className="skeleton-map"></div>
        <div className="skeleton-directions"></div>
      </div>
    </div>

    {/* Bottom CTA */}
    <div className="skeleton-bottom-cta">
      <div className="skeleton-cta-button"></div>
    </div>
  </div>
);

export default SalonProfileSkeleton;
